/**
 * Logging utility for Private LLM Chat
 * Provides configurable log levels via environment variables
 */

import { config } from './config';

// Log level constants with numeric values for comparison
const LOG_LEVELS = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
};

// Get the configured log level
const currentLevel = LOG_LEVELS[config.logLevel] ?? LOG_LEVELS.INFO;

/**
 * Format log message with timestamp and level
 * @param {string} level - Log level
 * @param {string} message - Log message
 * @param {*} data - Optional data to log
 * @returns {string} Formatted message
 */
const formatMessage = (level, message) => {
  const timestamp = new Date().toISOString();
  const prefix = `[${timestamp}] [${level}]`;
  return `${prefix} ${message}`;
};

/**
 * Log a message if the level is enabled
 * @param {string} level - Log level
 * @param {string} message - Log message
 * @param {*} data - Optional data to log
 * @param {Function} consoleFn - Console function to use
 */
const log = (level, message, data, consoleFn) => {
  if (LOG_LEVELS[level] >= currentLevel) {
    const formattedMessage = formatMessage(level, message);
    if (data !== undefined) {
      consoleFn(formattedMessage, data);
    } else {
      consoleFn(formattedMessage);
    }
  }
};

/**
 * Logger object with methods for each log level
 */
export const logger = {
  /**
   * Log debug message (development/troubleshooting)
   * @param {string} message - Log message
   * @param {*} [data] - Optional data to log
   */
  debug: (message, data) => {
    log('DEBUG', message, data, console.debug);
  },

  /**
   * Log info message (general information)
   * @param {string} message - Log message
   * @param {*} [data] - Optional data to log
   */
  info: (message, data) => {
    log('INFO', message, data, console.info);
  },

  /**
   * Log warning message (potential issues)
   * @param {string} message - Log message
   * @param {*} [data] - Optional data to log
   */
  warn: (message, data) => {
    log('WARN', message, data, console.warn);
  },

  /**
   * Log error message (errors and exceptions)
   * @param {string} message - Log message
   * @param {*} [data] - Optional error or data to log
   */
  error: (message, data) => {
    log('ERROR', message, data, console.error);
  },

  /**
   * Log API request (debug level)
   * @param {string} method - HTTP method
   * @param {string} url - Request URL
   * @param {*} [body] - Request body
   */
  apiRequest: (method, url, body) => {
    logger.debug(`API Request: ${method} ${url}`, body ? { body } : undefined);
  },

  /**
   * Log API response (debug level)
   * @param {string} method - HTTP method
   * @param {string} url - Request URL
   * @param {number} status - Response status code
   * @param {*} [data] - Response data
   */
  apiResponse: (method, url, status, data) => {
    const level = status >= 400 ? 'error' : 'debug';
    logger[level](`API Response: ${method} ${url} - ${status}`, data);
  },

  /**
   * Log user action (info level)
   * @param {string} action - Action name
   * @param {*} [details] - Action details
   */
  userAction: (action, details) => {
    logger.info(`User Action: ${action}`, details);
  },

  /**
   * Get current log level
   * @returns {string} Current log level name
   */
  getLevel: () => config.logLevel,

  /**
   * Check if a level is enabled
   * @param {string} level - Level to check
   * @returns {boolean} Whether the level is enabled
   */
  isLevelEnabled: (level) => {
    return LOG_LEVELS[level] >= currentLevel;
  },
};

export default logger;

